import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { SelectOption } from '../select-input/select-input.component';
import { GuidedSetupService } from '../../guided-setup/guided-setup.service';
import { AnalyticsEvent, InternalAnalyticsService } from '../../core/internal-analytics.service';
import { Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { UserService } from '../../core/user/user.service';

export interface PhoneForwardingProvider {
  id: string;
  name: string;
  unconditionalInstructions: string;
  conditionalInstructions?: string;
  disableInstructions: string;
  notes: string[];
  supportLink?: string;
}

export interface PhoneSystemType {
  id: string;
  name: string;
  providers: PhoneForwardingProvider[];
  generalNotes?: string[];
}

export interface PhoneForwardingConfig {
  systemTypes: PhoneSystemType[];
  generalNotes: string[];
}

// Animation for smooth content appearance
const fadeInUp = trigger('fadeInUp', [
  transition(':enter', [
    style({
      opacity: 0,
      transform: 'translateY(20px)'
    }),
    animate('300ms ease-out',
      style({
        opacity: 1,
        transform: 'translateY(0px)'
      })
    )
  ])
]);

@Component({
  selector: 'rosie-launch-instructions-overview',
  templateUrl: './launch-instructions-overview.component.html',
  styleUrls: ['./launch-instructions-overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [fadeInUp],
  standalone: false
})
export class LaunchInstructionsOverviewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() agentPhoneNumber: string = '';
  @Input() initialCallForwardingSelected: boolean = true;
  @Input() context: 'onboarding' | 'settings' = 'onboarding';
  // Hosts that render their own card title (ENG-2292 Go Live) hide the
  // built-in one.
  @Input() hideTitle = false;

  @Output() callForwardingOptionChanged = new EventEmitter<boolean>();
  @Output() openInstructionsClicked = new EventEmitter<void>();
  @Output() requestLocalAreaCodeClicked = new EventEmitter<string>();

  isCallForwardingSelected: boolean = true;
  phoneSystemTypeControl: FormControl = new FormControl(null);
  providerControl: FormControl = new FormControl(null);
  phoneSystemTypeOptions: SelectOption[] = [];
  providerOptions: SelectOption[] = [];
  currentInstructions: string = '';

  constructor(
    private guidedSetupService: GuidedSetupService,
    private analytics: InternalAnalyticsService,
    private router: Router,
    private user: UserService
  ) { }

  ngOnInit(): void {
    this.isCallForwardingSelected = this.initialCallForwardingSelected;
    this.initializePhoneSystemInstructions();

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedSystemType(): PhoneSystemType | null {
    const systemTypeId = this.phoneSystemTypeControl.value;
    return phoneForwardingConfig.systemTypes.find(type => type.id === systemTypeId) || null;
  }

  get selectedProvider(): PhoneForwardingProvider | null {
    const providerId = this.providerControl.value;
    const systemType = this.selectedSystemType;
    return systemType?.providers.find(provider => provider.id === providerId) || null;
  }

  get isUserOn7DayFreeTrialVersion(): boolean {
    return this.user.isUserOn7DayFreeTrialVersion;
  }

  get currentNotes(): string[] {
    const provider = this.selectedProvider;
    const systemType = this.selectedSystemType;

    const notes = [...(provider?.notes || [])];

    if (systemType?.generalNotes) {
      notes.push(...systemType.generalNotes);
    }

    return notes;
  }

  private getAnalyticsEvents() {
    if (this.context === 'settings') {
      return {
        phoneSystemTypeSelected: AnalyticsEvent.SettingsPhoneSystemTypeSelected,
        phoneProviderSelected: AnalyticsEvent.SettingsPhoneProviderSelected,
        useRosieNumberSelected: AnalyticsEvent.SettingsUseRosieNumberSelected
      };
    }
    return {
      phoneSystemTypeSelected: AnalyticsEvent.OnboardingPhoneSystemTypeSelected,
      phoneProviderSelected: AnalyticsEvent.OnboardingPhoneProviderSelected,
      useRosieNumberSelected: AnalyticsEvent.OnboardingUseRosieNumberSelected
    };
  }

  setActiveCallForwardingOption(value: boolean) {
    this.isCallForwardingSelected = value;
    this.callForwardingOptionChanged.emit(value);

    if (!value) {
      this.guidedSetupService.updateAgentConfig({ useRosieNumberSelected: true });

      const events = this.getAnalyticsEvents();
      this.analytics.trackEvent(events.useRosieNumberSelected);
    }
  }

  openInstructions() {
    this.openInstructionsClicked.emit();
  }

  onRequestLocalAreaCodeClicked() {
    this.requestLocalAreaCodeClicked.emit('launch-instructions-overview');
  }

  private initializePhoneSystemInstructions(): void {
    this.phoneSystemTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((phoneSystemType) => {
        this.handleProviderOptions();
        this.updateCurrentInstructions();
        if (phoneSystemType) {
          this.guidedSetupService.updateAgentConfig({ selectedPhoneSystemType: phoneSystemType });
          const events = this.getAnalyticsEvents();
          const selectedSystemType = this.selectedSystemType;
          this.analytics.trackEvent(events.phoneSystemTypeSelected, {
            phoneSystemType: phoneSystemType,
            phoneSystemTypeName: selectedSystemType?.name
          });
        }
      });

    this.providerControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((provider) => {
        this.updateCurrentInstructions();
        if (provider) {
          this.guidedSetupService.updateAgentConfig({ selectedPhoneProvider: provider });
          const events = this.getAnalyticsEvents();
          const selectedProvider = this.selectedProvider;
          this.analytics.trackEvent(events.phoneProviderSelected, {
            phoneProvider: provider,
            phoneProviderName: selectedProvider?.name,
            phoneSystemType: this.phoneSystemTypeControl.value
          });
        }
      });

    this.phoneSystemTypeOptions = phoneForwardingConfig.systemTypes.map(type => ({
      value: type.id,
      label: type.name
    }));

    this.handleProviderOptions();
    this.updateCurrentInstructions();
  }

  private handleProviderOptions(): void {
    const selectedSystemType = this.selectedSystemType;
    if (selectedSystemType) {
      this.providerOptions = selectedSystemType.providers.map(provider => ({
        value: provider.id,
        label: provider.name
      }));

      // Auto-select provider if there's only one option
      if (selectedSystemType.providers.length === 1) {
        this.providerControl.setValue(selectedSystemType.providers[0].id);
      } else {
        this.providerControl.setValue(null);
      }
    } else {
      this.providerOptions = [];
      this.providerControl.setValue(null);
    }
  }

  private updateCurrentInstructions(): void {
    const provider = this.selectedProvider;
    const phoneNumberWithoutCountryCode = this.agentPhoneNumber?.replace('+1', '');

    if (!provider) {
      this.currentInstructions = '';
      return;
    }

    const phoneNumber = phoneNumberWithoutCountryCode || '9543333343';
    let instructions = '';

    if (provider.unconditionalInstructions) {
      instructions += this.makeDialingCodesClickable(provider.unconditionalInstructions, phoneNumber);
    }

    if (provider.conditionalInstructions) {
      instructions += this.makeDialingCodesClickable(provider.conditionalInstructions, phoneNumber);
    }

    if (provider.disableInstructions) {
      instructions += this.makeDialingCodesClickable(provider.disableInstructions, phoneNumber);
    }

    this.currentInstructions = instructions;
  }

  private makeDialingCodesClickable(text: string, phoneNumber: string): string {
    let result = text.replace(/9543333343/g, phoneNumber);
    result = result.replace(/<strong>([*#\d]+(?:\*[*#\d]*)*(?:[*#\d]*)?)<\/strong>/g,
      (match, dialCode) => {
        return `<a href="tel:${dialCode}" class="phone-link">${dialCode}</a>`;
      });

    return result;
  }
}

export const phoneForwardingConfig: PhoneForwardingConfig = {
  systemTypes: [
    {
      id: 'cell-phone',
      name: 'Cell Phone Networks',
      providers: [
        {
          id: 'att',
          name: 'AT&T',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>**21*9543333343#</strong></p>
              <p>Press <strong>Call</strong>. You'll hear a confirmation tone.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Set each one up separately if you want Rosie to handle multiple scenarios:</p>
              <ul>
                <li>If busy: <strong>**67*9543333343#</strong></li>
                <li>If no answer: <strong>**61*9543333343#</strong></li>
                <li>If unreachable: <strong>**62*9543333343#</strong></li>
              </ul>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>##21#</strong></li>
                <li>Disable busy forwarding: <strong>##67#</strong></li>
                <li>Disable no answer forwarding: <strong>##61#</strong></li>
                <li>Disable unreachable forwarding: <strong>##62#</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'iPhone users can also go to Settings > Phone > Call Forwarding to enable unconditional forwarding (not conditional)',
            'Conditional forwarding requires dial codes',
            'Works on AT&T and most MVNOs like Cricket and Consumer Cellular',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ]
        },
        {
          id: 'verizon',
          name: 'Verizon',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>*729543333343</strong></p>
              <p>Press <strong>Call</strong>. You may hear a confirmation tone.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Set each one up separately:</p>
              <ul>
                <li>If busy: <strong>*909543333343</strong></li>
                <li>If no answer: <strong>*929543333343</strong></li>
              </ul>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>*73</strong></li>
              </ul>
              <p>➤ Verizon does not support disabling individual conditional rules.</p>
            </div>
          `,
          notes: [
            'Verizon does not support the iPhone Call Forwarding toggle—use the dialer',
            'Always test your setup by calling from another phone',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ]
        },
        {
          id: 't-mobile',
          name: 'T-Mobile',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>**21*9543333343#</strong></p>
              <p>Press <strong>Call</strong>.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Set each one up separately:</p>
              <ul>
                <li>If busy: <strong>**67*9543333343#</strong></li>
                <li>If no answer: <strong>**61*9543333343#</strong></li>
                <li>If unreachable: <strong>**62*9543333343#</strong></li>
              </ul>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>##21#</strong></li>
                <li>Disable busy forwarding: <strong>##67#</strong></li>
                <li>Disable no answer forwarding: <strong>##61#</strong></li>
                <li>Disable unreachable forwarding: <strong>##62#</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'iPhone users can use Settings > Phone > Call Forwarding to turn on unconditional forwarding',
            'Conditional setup requires dial codes',
            'Applies to T-Mobile and MVNOs like Metro, Mint, and Ultra',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ]
        },
        {
          id: 'boost-mobile',
          name: 'Boost Mobile',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>*729543333343</strong></p>
              <p>Press <strong>Call</strong>.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Not reliably supported. Most Boost users only have access to unconditional forwarding.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>*73</strong> or <strong>*720</strong></li>
              </ul>
              <p>No carrier-supported method to disable conditions separately</p>
            </div>
          `,
          notes: [
            'Boost Mobile does not support iPhone\'s call forwarding settings—use dial codes only',
            'Behavior may vary by device model or region',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ]
        },
        {
          id: 'mint-mobile',
          name: 'Mint Mobile',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>**21*9543333343#</strong></p>
              <p>Press <strong>Call</strong>.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Set each one up separately if you want Rosie to handle multiple scenarios:</p>
              <ul>
                <li>If busy: <strong>**67*9543333343#</strong></li>
                <li>If no answer: <strong>**61*9543333343#</strong></li>
                <li>If unreachable: <strong>**62*9543333343#</strong></li>
              </ul>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>##21#</strong></li>
                <li>Disable busy forwarding: <strong>##67#</strong></li>
                <li>Disable no answer forwarding: <strong>##61#</strong></li>
                <li>Disable unreachable forwarding: <strong>##62#</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'Mint uses T-Mobile\'s GSM network, so all standard forwarding codes work',
            'iPhone users can also use Settings > Phone > Call Forwarding for unconditional forwarding',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ]
        },
        {
          id: 'cricket-wireless',
          name: 'Cricket Wireless',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>**21*9543333343#</strong></p>
              <p>Press <strong>Call</strong>. You'll hear a confirmation tone.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Set each one up separately if you want Rosie to handle multiple scenarios:</p>
              <ul>
                <li>If busy: <strong>**67*9543333343#</strong></li>
                <li>If no answer: <strong>**61*9543333343#</strong></li>
                <li>If unreachable: <strong>**62*9543333343#</strong></li>
              </ul>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>##21#</strong></li>
                <li>Disable busy forwarding: <strong>##67#</strong></li>
                <li>Disable no answer forwarding: <strong>##61#</strong></li>
                <li>Disable unreachable forwarding: <strong>##62#</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'Cricket runs on AT&T\'s GSM network and supports standard GSM forwarding codes',
            'iPhone users can use Settings > Phone > Call Forwarding for unconditional forwarding',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ]
        },
        {
          id: 'consumer-cellular',
          name: 'Consumer Cellular',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>**21*9543333343#</strong></p>
              <p>Press <strong>Call</strong>. You'll hear a confirmation tone.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Set each one up separately if you want Rosie to handle multiple scenarios:</p>
              <ul>
                <li>If busy: <strong>**67*9543333343#</strong></li>
                <li>If no answer: <strong>**61*9543333343#</strong></li>
                <li>If unreachable: <strong>**62*9543333343#</strong></li>
              </ul>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>##21#</strong></li>
                <li>Disable busy forwarding: <strong>##67#</strong></li>
                <li>Disable no answer forwarding: <strong>##61#</strong></li>
                <li>Disable unreachable forwarding: <strong>##62#</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'Consumer Cellular typically uses AT&T or T-Mobile\'s network depending on your SIM',
            'GSM codes usually work, but test to confirm',
            'iPhone users can use Settings > Phone > Call Forwarding for unconditional setup',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ]
        },
        {
          id: 'sprint',
          name: 'Sprint',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <p>⚠️ Sprint has merged with T-Mobile, but if you're still on an older Sprint plan or device, use the instructions below.</p>
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>*729543333343</strong></p>
              <p>Press <strong>Call</strong>. You should hear a confirmation tone.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Set each one up separately:</p>
              <ul>
                <li>If busy: <strong>*739543333343</strong></li>
                <li>If no answer: <strong>*749543333343</strong></li>
                <li>If unreachable: <strong>*759543333343</strong></li>
              </ul>
              <p>⚠️ These may not be available on all Sprint plans or devices—test to confirm.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>*720</strong></li>
              </ul>
              <p>Conditional disable codes are not consistently supported on Sprint legacy plans</p>
            </div>
          `,
          notes: [
            'Sprint forwarding setup is done via dialer only—no app or settings-based option',
            'Most users on Sprint have been migrated to T-Mobile and should follow T-Mobile\'s setup instructions',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ]
        },
        {
          id: 'vonage',
          name: 'Vonage',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>*729543333343</strong></p>
              <p>Press <strong>1</strong> to confirm. You should hear a confirmation tone.</p>
            </div>
          `,
          conditionalInstructions: ``,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>*73</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'You can also enable or manage call forwarding from your Vonage Online Account under Features & Settings > Calling Features > Call Forwarding',
            'You can specify how many seconds your phone rings before forwarding',
            'Forwarding to international numbers may incur additional charges',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ],
          supportLink: 'https://support.vonage.com/'
        },
        {
          id: 'xfinity-mobile',
          name: 'Xfinity Mobile',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>*729543333343</strong></p>
              <p>Press <strong>Call</strong>. You should hear a confirmation tone.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding (No Answer Only):</h5>
              <p>Dial: <strong>*719543333343</strong></p>
              <p>Press <strong>Call</strong>. You should hear a confirmation tone.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>*73</strong></li>
              </ul>
              <p>No known support for disabling conditional forwarding separately</p>
            </div>
          `,
          notes: [
            'Xfinity Mobile only supports no answer conditional forwarding via *71',
            'You cannot adjust how long the phone rings before forwarding kicks in',
            'If dial codes don\'t work, contact Xfinity Mobile support to enable forwarding on your account',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ],
          supportLink: 'https://www.xfinity.com/support/'
        },
        {
          id: 'us-cellular',
          name: 'US Cellular',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>*729543333343</strong></p>
              <p>Press <strong>Call</strong>. You should hear a confirmation tone.</p>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <ul>
                <li>Busy: <strong>*909543333343</strong></li>
                <li>No Answer: <strong>*929543333343</strong></li>
              </ul>
              <p>Press <strong>Call</strong> after each code.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>*720</strong></li>
                <li>Disable busy forwarding: <strong>*900</strong></li>
                <li>Disable no answer forwarding: <strong>*920</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'These codes are entered via your phone\'s dialer and may not provide a confirmation message',
            'Ensure you\'re within U.S. Cellular\'s coverage area when setting up or disabling call forwarding',
            '⚠️ iPhone users: Make sure Live Voicemail is turned off'
          ],
          supportLink: 'https://www.uscellular.com/support/'
        },
        {
          id: 'Other',
          name: 'Other',
          unconditionalInstructions: `Not seeing your phone system's provider? Reach out to them directly for forwarding instructions. If you still need help, reach out to Rosie Support - we'd be happy to assist.`,
          conditionalInstructions: ``,
          disableInstructions: ``,
          notes: []
        }
      ]
    },
    {
      id: 'voip',
      name: 'VOIP & Web Phone Systems',
      providers: [
        {
          id: 'openphone',
          name: 'OpenPhone',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <p><strong>📖 <a href="https://support.openphone.com/hc/en-us/articles/1500009886441-How-to-forward-your-OpenPhone-calls-to-a-different-number" target="_blank">Full setup instructions from OpenPhone support</a></strong></p>
              <h5>Unconditional Forwarding (All Calls):</h5>
              <ol>
                <li>Click <strong>Settings</strong> from the left-hand menu</li>
                <li>Under <strong>Workspace</strong>, click <strong>Phone Numbers</strong></li>
                <li>Select the phone number you want to forward</li>
                <li>In the <strong>Call flow</strong> section, select <strong>Forward all calls</strong></li>
                <li>Add your Rosie number: <strong>9543333343</strong> and click <strong>Forward</strong></li>
              </ol>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>To forward calls only at specific times or if unanswered:</p>
              <p><strong>For forwarding outside business hours:</strong></p>
              <ul>
                <li>Go to <strong>Call flow settings</strong></li>
                <li>Click <strong>Business hours</strong></li>
                <li>Under <strong>Outside business hours</strong>, add the Rosie number you want to forward to</li>
              </ul>
              <p><strong>For forwarding during business hours (unanswered calls):</strong></p>
              <ul>
                <li>In <strong>Call flow</strong>, scroll to the <strong>Unanswered calls</strong> section</li>
                <li>Under <strong>When no one answers</strong>, select <strong>Forward to another number</strong></li>
                <li>Enter your Rosie number: <strong>9543333343</strong></li>
              </ul>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Return to your call flow settings and remove or turn off the forwarding rule</li>
              </ul>
            </div>
          `,
          notes: [
            'OpenPhone supports granular routing based on time of day and availability',
            'Forwarding settings apply per phone number'
          ],
          supportLink: 'https://support.openphone.com/hc/en-us/articles/1500009886441-How-to-forward-your-OpenPhone-calls-to-a-different-number'
        },
        {
          id: 'ringcentral',
          name: 'RingCentral',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <p><strong>📖 <a href="https://support.ringcentral.com/article-v2/Forwarding-calls-external-number.html?brand=RC_US&language=en_US&product=RingEX" target="_blank">Full setup instructions from RingCentral support</a></strong></p>
              <h5>Unconditional Forwarding (All Calls):</h5>
              <ol>
                <li>Click your <strong>profile picture</strong> at the top left</li>
                <li>Click <strong>Call rules</strong></li>
                <li>Toggle on <strong>Forward all calls</strong></li>
                <li>Click <strong>Save</strong></li>
              </ol>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>To forward calls based on a schedule:</p>
              <ol>
                <li>Click your <strong>profile picture</strong> at the top left</li>
                <li>Click <strong>Call rules</strong></li>
                <li>Click <strong>Add schedule</strong></li>
                <li>Use the date and time selectors to set when you want calls forwarded</li>
                <li>Click <strong>Save</strong></li>
              </ol>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Go to <strong>Call rules</strong> and toggle off <strong>Forward all calls</strong> or remove any active schedule</li>
              </ul>
            </div>
          `,
          notes: [
            'RingCentral supports detailed routing and scheduling logic via the app or admin portal',
            'Settings apply per user or extension'
          ],
          supportLink: 'https://support.ringcentral.com/article-v2/Forwarding-calls-external-number.html?brand=RC_US&language=en_US&product=RingEX'
        },
        {
          id: 'google-voice',
          name: 'Google Voice',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>To forward calls from Google Voice to Rosie, you'll need to link your Rosie number and verify it.</p>
              <ol>
                <li>Log into your <strong>Rosie Admin</strong> and go to <strong>Account > Account Settings</strong></li>
                <li>Enable the <strong>"Call Forwarding Verification - SMS authentication code"</strong> toggle</li>
              </ol>
              <p>You can setup and configure call forwarding from Google Voice desktop browser or from within Google Voice's device app.</p>
              
              <p><strong>For Desktop -</strong></p>
              <ol>
                <li>In a new tab, go to <strong>Google Voice</strong></li>
                <li>In the top right, click the <strong>Settings gear icon</strong></li>
                <li>Under <strong>Account > Linked numbers</strong>, click <strong>New linked number</strong></li>
                <li>Enter your Rosie number: <strong>9543333343</strong> and click <strong>Send code</strong></li>
                <li>Return to the <strong>Rosie Admin > Account Settings</strong> tab and click <strong>View text messages</strong></li>
                <li>This will open a window displaying any verification codes sent to your Rosie number</li>
                <li>Back in Google Voice, enter the code received and click <strong>Verify</strong></li>
                <li>Once verified, go to <strong>Settings > Calls</strong></li>
                <li>Under <strong>Incoming calls</strong>, ensure <strong>Call Forwarding</strong> is enabled for your linked Rosie phone number</li>
                <li>Return to the <strong>Rosie Admin</strong> and disable verification code toggle</li>
              </ol>
              
              <p><strong>For Device App -</strong></p>
              <ol>
                <li>Open <strong>Google Voice app</strong> on your device and go to settings (three bars hamburger menu in top left corner)</li>
                <li>Tap into <strong>'Devices and numbers'</strong></li>
                <li>Tap <strong>'New linked number'</strong></li>
                <li>Input your Rosie phone number: <strong>9543333343</strong> and <strong>uncheck the 'number belongs to this device' option</strong></li>
                <li>click <strong>Send code</strong></li>
                <li>Return to the <strong>Rosie Admin > Account Settings</strong> tab and click <strong>View text messages</strong></li>
                <li>This will open a window displaying any verification codes sent to your Rosie number</li>
                <li>Remember to disable verification code toggle</li>
                <li>Back in Google Voice App, enter the code received and tap <strong>Verify</strong></li>
                <li>Google voice automatically enables call forwarding when you link a number, however it's best to double check</li>
                <li>In the Google Voice app, tap back out of Devices and numbers</li>
                <li>Still within settings, tap into <strong>'Incoming Calls'</strong></li>
                <li>Ensure call forwarding toggle is enabled for your Rosie phone number</li>
              </ol>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Not supported. Google Voice only allows full (unconditional) call forwarding.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Toggle it off under <strong>Settings > Calls > Incoming calls</strong></li>
                <li>Or go to <strong>Settings > Linked numbers</strong> and remove the Rosie number</li>
              </ul>
            </div>
          `,
          notes: [
            'You must verify your Rosie number before Google Voice will forward to it',
            'Setup must be completed via web browser, not the mobile app',
            'Call forwarding options vary depending on if you have the paid or free version',
            'If your Google Voice number is displaying on all of your Rosie calls, ensure you have the following setting disabled in Google Voice'
          ],
          supportLink: 'https://support.google.com/voice/'
        },
        {
          id: 'grasshopper',
          name: 'Grasshopper',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <p><strong>📖 <a href="https://grasshopper.com/blog/how-to-forward-calls/" target="_blank">Full setup instructions from Grasshopper support</a></strong></p>
              <h5>Unconditional Forwarding (All Calls):</h5>
              <ol>
                <li>Log into your <strong>Grasshopper account</strong></li>
                <li>Go to <strong>Settings > Call Forwarding Settings > Extensions</strong></li>
                <li>Find the number or extension you want to forward and click <strong>Edit</strong></li>
                <li>Enter your Rosie number: <strong>9543333343</strong> as the forwarding destination (can be mobile, landline, or voicemail)</li>
                <li>Choose how Grasshopper should handle the call (e.g., announce it before pickup)</li>
                <li>Select a forwarding schedule (e.g., 24/7, weekends, or custom hours)</li>
                <li>Save your settings</li>
                <li>Make a test call to confirm forwarding is working</li>
              </ol>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Grasshopper allows conditional routing based on time and call rules. Use the schedule settings during setup to apply forwarding only at specific times.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Go to the same <strong>Call Forwarding Settings > Extensions</strong> section</li>
                <li>Click <strong>Edit</strong> and remove the Rosie number or adjust the time schedule to disable routing</li>
              </ul>
            </div>
          `,
          notes: [
            'Grasshopper forwarding works across mobile, desktop, and landline',
            'You can forward to multiple numbers or configure advanced handling if needed'
          ],
          supportLink: 'https://grasshopper.com/blog/how-to-forward-calls/'
        },
        {
          id: 'dialpad',
          name: 'DialPad',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <p><strong>📖 <a href="https://help.dialpad.com/docs/manage-your-dialpad-devices" target="_blank">Full setup instructions from Dialpad support</a></strong></p>
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>To forward calls from Dialpad to Rosie, you'll need to link and verify your Rosie number.</p>
              <ol>
                <li>Log into your <strong>Rosie Admin</strong> and go to the <strong>Account</strong> tab</li>
                <li>Enable the setting: <strong>"Call Forwarding Verification – DialPad"</strong></li>
                <li>Log into your <strong>Dialpad account</strong></li>
                <li>Go to <strong>Your Settings > Your Devices</strong></li>
                <li>Click <strong>Add new</strong>, then select <strong>Add a forwarding number</strong></li>
                <li>Enter your <strong>Rosie number: 9543333343</strong> and click <strong>Verify</strong></li>
                <li><em>Dialpad will call your Rosie number and she will press 1 to verify the setup</em></li>
                <li>Choose how you want forwarding to work under <strong>Advanced Settings</strong> (e.g., always forward, forward when unavailable, etc.)</li>
              </ol>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Dialpad allows advanced call routing (e.g., based on availability, business hours, or custom logic). Configure these options under <strong>Call Handling & Forwarding</strong> in your user or company settings.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Return to <strong>Your Settings > Your Devices</strong> and remove or disable the Rosie forwarding number</li>
              </ul>
            </div>
          `,
          notes: [
            'Verification via Rosie Admin is required to activate forwarding',
            'Dialpad also supports call routing logic through departments, call queues, and ring rules'
          ],
          supportLink: 'https://help.dialpad.com/docs/manage-your-dialpad-devices'
        },
        {
          id: 'vonage-business',
          name: 'Vonage Business Communications',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <p><strong>📖 <a href="https://businesssupport.vonage.com/articles/answer/Call-Forwarding-24813" target="_blank">Full setup instructions from Vonage Business Communications support</a></strong></p>
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p><strong>For Admins:</strong></p>
              <ol>
                <li>Sign in to the <strong>Admin Portal</strong></li>
                <li>Navigate to <strong>Phone System > Extensions</strong></li>
                <li>Hover over the desired extension and click the <strong>Pencil</strong> icon</li>
                <li>Click <strong>Call Forwarding</strong>, then toggle <strong>Forward Calls</strong> to enabled</li>
                <li>Select <strong>Send To Ext. or Number</strong>, enter the forwarding number: <strong>9543333343</strong>, and choose your Caller ID preference</li>
                <li>Click <strong>Save</strong></li>
              </ol>
              <p><strong>For End Users:</strong></p>
              <ol>
                <li>Sign in to your <strong>Online Account</strong></li>
                <li>Click your <strong>Avatar</strong>, then select <strong>Settings</strong></li>
                <li>Click <strong>Call Forwarding</strong> and enable <strong>Forward All Calls</strong></li>
                <li>Enter the forwarding number: <strong>9543333343</strong> and select your Caller ID preference</li>
                <li>Click <strong>Save</strong></li>
              </ol>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Utilize features like <strong>Follow Me</strong> and <strong>Simultaneous Ring</strong> to forward calls based on specific conditions. These settings can be configured in the <strong>Call Forwarding</strong> section of your account.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Admins: Toggle off <strong>Forward Calls</strong> in the Admin Portal</li>
                <li>End Users: In your account settings, select an alternative option such as <strong>Voicemail</strong>, <strong>Follow Me</strong>, or <strong>Simultaneous Ring</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'Avoid forwarding calls to a mobile number that also uses the Vonage Mobile App to prevent duplicate call notifications'
          ],
          supportLink: 'https://businesssupport.vonage.com/articles/answer/Call-Forwarding-24813'
        },
        {
          id: 'zoom-phone',
          name: 'Zoom Phone',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <p><strong>📖 <a href="https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0069180" target="_blank">Full setup instructions from Zoom Phone support</a></strong></p>
              <h5>Unconditional Forwarding (All Calls):</h5>
              <ol>
                <li>Sign in to the <strong>Zoom web portal</strong></li>
                <li>Click <strong>Phone</strong> in the navigation menu</li>
                <li>Go to the <strong>Settings</strong> tab</li>
                <li>Under <strong>Business Hours</strong> or <strong>Closed Hours</strong>, click <strong>Edit</strong> next to <strong>Call Handling</strong></li>
                <li>Click <strong>Add Phone Number</strong> to forward to an external number</li>
                <li>Enter the country/region, phone number: <strong>9543333343</strong>, and a description</li>
                <li>Click <strong>Save</strong></li>
              </ol>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Zoom Phone supports forwarding based on:</p>
              <ul>
                <li>Business Hours</li>
                <li>Closed Hours</li>
                <li>Break Hours</li>
                <li>Holiday Hours</li>
              </ul>
              <p>You can set different call handling rules for each time period by editing them individually in your settings.</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Return to the <strong>Call Handling</strong> settings for the relevant time period</li>
                <li>Remove the external number or toggle off the forwarding option</li>
              </ul>
            </div>
          `,
          notes: [
            'If you\'re unable to add an external number, your Zoom admin may need to allow it'
          ],
          supportLink: 'https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0069180'
        },
        {
          id: 'freedomvoice',
          name: 'FreedomVoice',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <p><strong>📖 <a href="https://static.freedomvoice.com/userGuides/VOuser/UserGuide.pdf" target="_blank">Full setup instructions from FreedomVoice support</a></strong></p>
              <h5>Unconditional Forwarding (All Calls):</h5>
              <ol>
                <li>Log in to your <strong>WebLink Control Panel</strong></li>
                <li>Navigate to <strong>Settings > Call Delivery > Call Forwarding</strong></li>
                <li>Select your default mailbox (e.g., 801)</li>
                <li>Click <strong>Add</strong> to create a new forwarding entry</li>
                <li>Enter your Rosie number: <strong>9543333343</strong> as the forwarding destination</li>
                <li>Optionally, click <strong>Add Call Time</strong> to set specific days/times for forwarding</li>
                <li>Click <strong>Save</strong></li>
              </ol>
            </div>
          `,
          conditionalInstructions: `
            <div class="forwarding-section">
              <h5>Conditional Forwarding:</h5>
              <p>Various settings may be available in configuration settings</p>
            </div>
          `,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>In the <strong>Call Forwarding</strong> section, uncheck the <strong>Active</strong> box next to the forwarding entry</li>
                <li>Alternatively, remove the forwarding entry by clicking <strong>Remove</strong></li>
              </ul>
            </div>
          `,
          notes: [],
          supportLink: 'https://static.freedomvoice.com/userGuides/VOuser/UserGuide.pdf'
        },
        {
          id: 'Other',
          name: 'Other',
          unconditionalInstructions: `Not seeing your phone system's provider? Reach out to them directly for forwarding instructions. If you still need help, reach out to Rosie Support - we'd be happy to assist.`,
          conditionalInstructions: ``,
          disableInstructions: ``,
          notes: []
        }
      ]
    },
    {
      id: 'landline',
      name: 'Landline',
      providers: [
        {
          id: 'landline-general',
          name: 'General Instructions',
          unconditionalInstructions: `
            <div class="forwarding-section">
              <h5>Unconditional Forwarding (All Calls):</h5>
              <p>Dial: <strong>*729543333343</strong></p>
              <p>Press <strong>Call</strong> or wait for confirmation (you may hear a tone or voice prompt)</p>
            </div>
          `,
          conditionalInstructions: ``,
          disableInstructions: `
            <div class="forwarding-section">
              <h5>To Disable Forwarding:</h5>
              <ul>
                <li>Disable all forwarding configuration: <strong>*73</strong></li>
              </ul>
            </div>
          `,
          notes: [
            'These dial codes work for most traditional landline providers in the U.S. (e.g., AT&T Landline, CenturyLink, Frontier)',
            'If *72 or *73 doesn\'t work, contact your landline provider\'s support or check your phone bill for the carrier name',
            'These codes are entered just like making a phone call—no need for any special settings'
          ]
        }
      ]
    }
  ],
  generalNotes: [
    'Test forwarding with a known number before relying on it',
    'Some carriers may charge fees for call forwarding',
    'Forwarding settings can sometimes reset during network updates'
  ]
}; 